import { useQuery } from '@tanstack/react-query';
import { useState, useCallback } from 'react';
import { pedidosApi } from '../api/pedidos';
import type { PedidosFilters, PainelRegiao } from '../types/pedidos';

// Filtros internos do hook (espelham PedidosFilters + filtroRegiao só-front).
interface ExtendedFilters extends PedidosFilters {
  filtroRegiao?: string[];
}

export const usePedidos = () => {

  const [filters, setFiltersState] = useState<ExtendedFilters>({
    filtroCliente: '',
    filtroVendedor: '',
    filtroStatus: '',
    filtroRegiao: [],
    // filtroEmpresas vazio = todas. Default sem nenhuma selecionada no UI = todas.
    filtroEmpresas: [],
  });

  const [autorefresh, setAutorefresh] = useState(false);

  const {
    data: pedidosResponse,
    error,
    isLoading: loading,
    refetch
  } = useQuery({
    queryKey: ['pedidos', 'regiao', filters],
    queryFn: () => pedidosApi.getPedidosPorRegiao(filters),
    refetchInterval: autorefresh ? 30000 : false,
    select: (response) => response.data
  });

  const pedidos: PainelRegiao[] = pedidosResponse?.data || [];
  const errorMessage = error instanceof Error ? error.message : null;

  const setFilters = {
    setQ: useCallback((value: string) => {
      setFiltersState(prev => ({ ...prev, filtroCliente: value }));
    }, []),

    setFRegiao: useCallback((value: string[]) => {
      setFiltersState(prev => ({ ...prev, filtroRegiao: value }));
    }, []),

    setFConf: useCallback((value: string) => {
      setFiltersState(prev => ({ ...prev, filtroStatus: value }));
    }, []),

    setFEmpresas: useCallback((value: string[]) => {
      setFiltersState(prev => ({ ...prev, filtroEmpresas: value }));
    }, []),
  };

  const fetchData = useCallback(() => {
    refetch();
  }, [refetch]);

  return {
    pedidos,
    error: errorMessage,
    loading,
    fetchData,
    filters: {
      q: filters.filtroCliente || '',
      fRegiao: filters.filtroRegiao || [],
      fConf: filters.filtroStatus || '',
      fEmpresas: filters.filtroEmpresas || [],
    },
    setFilters,
    autorefresh,
    setAutorefresh
  };
};

export const useProdutosPedido = (pedidoCodigo: string, empresa?: string) => {
  return useQuery({
    queryKey: ['pedidos', 'produtos', pedidoCodigo, empresa],
    queryFn: () => pedidosApi.getProdutosPedido(pedidoCodigo, empresa),
    enabled: !!pedidoCodigo,
    select: (response) => response.data.data
  });
};

export const useContasReceber = (clienteCodigo: string) => {
  return useQuery({
    queryKey: ['pedidos', 'contas-receber', clienteCodigo],
    queryFn: () => pedidosApi.getContasReceber(clienteCodigo),
    enabled: !!clienteCodigo,
    select: (response) => response.data.data
  });
};
